/**
 * WhatsApp-style notification sounds using Web Audio API.
 * No external files needed — generates tones programmatically.
 */

let audioCtx: AudioContext | null = null;

function getCtx(): AudioContext {
  if (!audioCtx) audioCtx = new AudioContext();
  if (audioCtx.state === 'suspended') audioCtx.resume();
  return audioCtx;
}

/** Helper: create oscillator + gain connected to destination */
function tone(
  ctx: AudioContext,
  type: OscillatorType,
  freq: number,
  startTime: number,
  duration: number,
  volume: number,
  freqEnd?: number,
) {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.connect(gain);
  gain.connect(ctx.destination);

  osc.type = type;
  osc.frequency.setValueAtTime(freq, startTime);
  if (freqEnd) osc.frequency.exponentialRampToValueAtTime(freqEnd, startTime + duration * 0.8);

  gain.gain.setValueAtTime(0, startTime);
  gain.gain.linearRampToValueAtTime(volume, startTime + 0.005); // fast attack
  gain.gain.setValueAtTime(volume, startTime + duration * 0.6);
  gain.gain.exponentialRampToValueAtTime(0.001, startTime + duration);

  osc.start(startTime);
  osc.stop(startTime + duration);
}

/** Sonido de mensaje enviado — "pop" corto ascendente */
export function playSendSound() {
  try {
    const ctx = getCtx();
    const now = ctx.currentTime;
    tone(ctx, 'sine', 600, now, 0.1, 0.12, 1200);
  } catch { /* silent */ }
}

/**
 * Sonido de mensaje recibido — estilo WhatsApp Web.
 * Dos notas musicales claras: C6 (1047Hz) → G5 (784Hz) con armónicos suaves.
 * Similar al "ding-dong" de WhatsApp Web.
 */
export function playReceiveSound() {
  try {
    const ctx = getCtx();
    const now = ctx.currentTime;

    // Nota 1: C6 (1047 Hz) — ataque rápido, decay suave
    const osc1 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    osc1.connect(gain1);
    gain1.connect(ctx.destination);
    osc1.type = 'sine';
    osc1.frequency.setValueAtTime(1047, now);
    gain1.gain.setValueAtTime(0, now);
    gain1.gain.linearRampToValueAtTime(0.18, now + 0.005);
    gain1.gain.setValueAtTime(0.18, now + 0.06);
    gain1.gain.exponentialRampToValueAtTime(0.001, now + 0.25);
    osc1.start(now);
    osc1.stop(now + 0.25);

    // Armónico suave de nota 1 (una octava arriba, bajo volumen)
    const osc1h = ctx.createOscillator();
    const gain1h = ctx.createGain();
    osc1h.connect(gain1h);
    gain1h.connect(ctx.destination);
    osc1h.type = 'sine';
    osc1h.frequency.setValueAtTime(2094, now);
    gain1h.gain.setValueAtTime(0, now);
    gain1h.gain.linearRampToValueAtTime(0.04, now + 0.005);
    gain1h.gain.exponentialRampToValueAtTime(0.001, now + 0.15);
    osc1h.start(now);
    osc1h.stop(now + 0.15);

    // Nota 2: G5 (784 Hz) — ligeramente retrasada
    const t2 = now + 0.12;
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.connect(gain2);
    gain2.connect(ctx.destination);
    osc2.type = 'sine';
    osc2.frequency.setValueAtTime(784, t2);
    gain2.gain.setValueAtTime(0, t2);
    gain2.gain.linearRampToValueAtTime(0.15, t2 + 0.005);
    gain2.gain.setValueAtTime(0.15, t2 + 0.08);
    gain2.gain.exponentialRampToValueAtTime(0.001, t2 + 0.35);
    osc2.start(t2);
    osc2.stop(t2 + 0.35);

    // Armónico suave de nota 2
    const osc2h = ctx.createOscillator();
    const gain2h = ctx.createGain();
    osc2h.connect(gain2h);
    gain2h.connect(ctx.destination);
    osc2h.type = 'sine';
    osc2h.frequency.setValueAtTime(1568, t2);
    gain2h.gain.setValueAtTime(0, t2);
    gain2h.gain.linearRampToValueAtTime(0.03, t2 + 0.005);
    gain2h.gain.exponentialRampToValueAtTime(0.001, t2 + 0.2);
    osc2h.start(t2);
    osc2h.stop(t2 + 0.2);

  } catch { /* silent */ }
}
